import { useEffect } from "react";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";

const sections = [
  {
    title: "個人情報の定義",
    content:
      "「個人情報」とは、個人情報保護法に定める「個人情報」を指し、生存する個人に関する情報であって、当該情報に含まれる氏名、住所、電話番号、メールアドレスその他の記述等により特定の個人を識別できるものをいいます。",
  },
  {
    title: "個人情報の収集方法",
    content:
      "当社は、お客様がお問い合わせフォーム、お電話、メール等を通じてサービスをお申し込みいただく際に、お名前、会社名、電話番号、メールアドレス、ご住所等の個人情報を収集させていただくことがあります。",
  },
  {
    title: "個人情報の利用目的",
    content:
      "当社は、お客様からお預かりした個人情報を以下の目的の範囲内で利用いたします。",
    list: [
      "お見積もりのご提示およびご連絡のため",
      "工事・サービスに関するご案内およびご契約の履行のため",
      "アフターフォロー・保証対応のため",
      "お客様からのお問い合わせに対する回答のため",
      "当社サービスの品質向上および新サービス開発のため",
      "法令に基づく対応が必要となる場合",
    ],
  },
  {
    title: "個人情報の安全管理",
    content:
      "当社は、お預かりした個人情報の漏洩、滅失または毀損の防止その他個人情報の安全管理のために必要かつ適切な措置を講じます。個人情報を取り扱う従業員に対しても、定期的な教育を実施し、適切な管理に努めます。",
  },
  {
    title: "個人情報の第三者提供",
    content:
      "当社は、お客様の同意を得ることなく、第三者に個人情報を提供することはありません。ただし、以下のいずれかに該当する場合を除きます。",
    list: [
      "お客様ご本人の同意がある場合",
      "法令に基づく場合",
      "人の生命、身体または財産の保護のために必要がある場合であって、お客様の同意を得ることが困難な場合",
      "公衆衛生の向上または児童の健全な育成の推進のために特に必要がある場合であって、お客様の同意を得ることが困難な場合",
      "国の機関もしくは地方公共団体またはその委託を受けた者が法令の定める事務を遂行することに対して協力する必要がある場合",
    ],
  },
  {
    title: "個人情報の開示・訂正・削除",
    content:
      "お客様ご本人から、当社が保有するご自身の個人情報について、開示、訂正、追加、削除、利用停止等をご依頼いただいた場合、ご本人確認をさせていただいたうえで、法令に基づき速やかに対応いたします。",
  },
  {
    title: "Cookie（クッキー）について",
    content:
      "当社のウェブサイトでは、サービスの利便性向上やアクセス解析を目的としてCookieを使用する場合があります。お客様はブラウザの設定によりCookieの受け入れを拒否することも可能ですが、その場合、一部のサービスが正しく機能しない可能性があります。",
  },
  {
    title: "お問い合わせ窓口",
    content:
      "個人情報の取扱いに関するお問い合わせは、下記の窓口までご連絡ください。",
    contact: true,
  },
];

const siteUrl = import.meta.env.VITE_PUBLIC_SITE_URL || "https://www.wind-service0120.com";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "プライバシーポリシー | 株式会社Wind",
  "description":
    "株式会社Windのプライバシーポリシー。お客様の個人情報の取扱いについて定めています。",
  "url": `${siteUrl}/privacy-policy`,
  "isPartOf": {
    "@type": "WebSite",
    "name": "株式会社Wind",
    "url": siteUrl,
  },
};

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "プライバシーポリシー | 株式会社Wind";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "株式会社Windのプライバシーポリシー。お客様の個人情報の取扱いについて定めています。"
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", `${siteUrl}/privacy-policy`);
    }
    return () => {
      document.title = "株式会社Wind | 世田谷区・川崎市の原状回復・リフォーム・ハウスクリーニング";
      if (metaDesc) {
        metaDesc.setAttribute(
          "content",
          "東京都世田谷区・目黒区、神奈川県川崎市麻生区・多摩区を中心に、原状回復工事、内装リフォーム、ハウスクリーニングまで総合的に承る株式会社Wind。地域密着のスピードと丁寧な仕上げで住まいの価値を最大化します。"
        );
      }
      if (canonical) {
        canonical.setAttribute("href", siteUrl + "/");
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-950 font-sans">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Header />

      <main role="main" aria-label="メインコンテンツ" className="pt-24 md:pt-32 pb-20 md:pb-32 px-6 md:px-16 lg:px-24">
        <div className="max-w-3xl mx-auto">
          {/* Page header */}
          <div className="mb-14 md:mb-20">
            <div className="inline-flex items-center gap-3 mb-4">
              <span className="w-10 h-px bg-primary-500"></span>
              <span className="text-xs tracking-[0.3em] uppercase text-primary-700 font-medium">
                Privacy Policy
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              プライバシーポリシー
            </h1>
            <p className="text-sm text-foreground-600">
              制定日：2022年4月1日　｜　最終改定日：2026年6月25日
            </p>
          </div>

          {/* Intro */}
          <p className="text-foreground-700 text-base leading-relaxed mb-12">
            株式会社Wind（以下「当社」といいます）は、お客様の個人情報の重要性を深く認識し、
            個人情報の保護に関する法律（個人情報保護法）その他関連法令を遵守するとともに、
            以下のプライバシーポリシーに従い、お客様の個人情報を適切に取り扱うことをお約束いたします。
          </p>

          {/* Sections */}
          <div className="space-y-12 md:space-y-16">
            {sections.map((section, idx) => (
              <section key={idx}>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5 flex items-start gap-3">
                  <span className="text-primary-500 shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-line"></i>
                  </span>
                  {section.title}
                </h2>
                <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
                  {section.content}
                </p>
                {section.list && (
                  <ul className="list-disc list-inside space-y-2 text-foreground-700 text-sm md:text-base leading-relaxed ml-2">
                    {section.list.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                )}
                {section.contact && (
                  <div className="mt-5 p-6 md:p-8 rounded-2xl bg-background-100/70 border border-background-200/70">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <p className="text-sm font-bold text-foreground-950 mb-1">
                          株式会社Wind
                        </p>
                        <p className="text-sm text-foreground-600">
                          〒156-0043 東京都世田谷区松原3-30-10 双葉屋ビル 2F
                        </p>
                      </div>
                      <div className="flex flex-col gap-1 text-sm text-foreground-700">
                        <span className="flex items-center gap-2">
                          <i className="ri-phone-line text-primary-500"></i>
                          03-6379-2823
                        </span>
                        <span className="flex items-center gap-2">
                          <i className="ri-mail-line text-primary-500"></i>
                          info@wind-service0120.com
                        </span>
                      </div>
                    </div>
                    <p className="mt-4 text-xs text-foreground-500">
                      受付時間：9:00 〜 18:00（定休日：水曜日・日曜日・祝日）
                    </p>
                  </div>
                )}
              </section>
            ))}
          </div>

          {/* Back link */}
          <div className="mt-16 pt-8 border-t border-background-200/70">
            <a
              href="/"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              トップページに戻る
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}